/**
 * Service-scoped WAAP hooks. WAAP is a standalone product (the Cobra service)
 * with its own domain binding namespace (type='waap'), fully separate from the
 * CDN service views — so the WAAP console sources its website list HERE, never
 * from /services/:id/cdn/domains (which lists type='cdn' bindings and is empty
 * for a WAAP service).
 *   GET /api/v3/services/:id/waap/domains
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';

// Mirrors of the manticore types this hook returns and takes (`src/api/manticore/_shared.ts`,
// `domains.ts`), so the file does not import the app. The app's types are structurally the same.
interface DomainListPagination {
  page: number;
  per_page: number;
  total: number;
}
interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
  pagination?: DomainListPagination;
  message?: string;
}
interface CDNBinding {
  binding_id: string;
  provider: string;
  provider_id: string;
  zone_id?: string;
  uid?: string;
  status?: string;
  enabled?: boolean;
}
interface CDNDomain {
  id?: string;
  domain: string;
  cname_target?: string;
  cname?: string;
  origin?: string;
  status?: 'active' | 'deploying' | 'suspended' | 'disabled' | 'pending' | string;
  enabled?: boolean;
  tags?: string[];
  bindings?: CDNBinding[];
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}
interface DomainListQuery {
  page: number;
  perPage: number;
  search?: string;
  enabled?: boolean;
}

const serviceApiV3 = getHttpClient('serviceApiV3');

export interface ServiceWaapDomainList {
  data: CDNDomain[];
  pagination: DomainListPagination;
}

/** Server-paginated WAAP domain (website) list scoped to one WAAP service.
 *  Same return shape as useServiceCdnDomainsPaged so callers swap transparently. */
export function useServiceWaapDomainsPaged(serviceId: string, q: DomainListQuery) {
  return useQuery({
    queryKey: ['waap', 'service', serviceId, 'domains', 'paged', q.page, q.perPage, q.search ?? ''],
    enabled: !!serviceId && (q.enabled ?? true),
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async (): Promise<ServiceWaapDomainList> => {
      const params = new URLSearchParams();
      params.set('page', String(q.page));
      params.set('per_page', String(q.perPage));
      if (q.search) params.set('search', q.search);
      const res = await serviceApiV3.get<Envelope<CDNDomain[]>>(
        `/services/${serviceId}/waap/domains?${params.toString()}`,
      );
      return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { page: q.page, per_page: q.perPage, total: 0 },
      };
    },
  });
}

export interface DeleteWaapServiceResult {
  service_name?: string;
}

/** Delete a WAAP service (removes the service row + its provisioned zones).
 *  DELETE /api/v3/services/:id — the generic service-delete endpoint. */
export function useDeleteWaapService(
  serviceId: string,
  options?: UseMutationOptions<DeleteWaapServiceResult, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DeleteWaapServiceResult> => {
      const res = await serviceApiV3.delete<{ data?: DeleteWaapServiceResult }>(
        `/services/${serviceId}`,
      );
      return res.data?.data ?? {};
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['my-services'] });
      options?.onSuccess?.(...args);
    },
  });
}
